import { type FC, Fragment, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue } from 'recoil'

import { pageZoomAtom } from '@/atoms/editorAtoms'
import { itemIdAtom, selectedItemsAtom } from '@/atoms/_editorAtoms'
import { itemControlStateAtomFamily } from '@/atoms/itemControlAtoms'
import type { ItemControlState } from '@/types/itemControls'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { Icon } from '@/components/priority/Icon/Icon'
import {
  HideInPdfBadge,
  ItemWrapper,
  WarningBar,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import { ClassicWarningBar } from '@/components/page/Items/ItemControlOverlay/classic/ClassicItemControlOverlay.styled'
import { EditorTextBoxConvertModal } from '@/components/page/Items/ItemControlOverlay/classic/EditorTextBoxConvertModal'
import { WarningIndicators } from '@/components/page/Items/ItemControlOverlay/controls/WarningIndicators'
import { SelectedItemControls } from '@/components/page/Items/ItemControlOverlay/SelectedItemControls'
import {
  useEditorSetTextBoxSizing,
  type EditorTextSizingPatch,
} from '@/hooks/editor/useEditorSetTextBoxSizing'
import { isEditorItemType } from '@/utils/editor-text-box/editorTextBoxAdapter'

type ProbeWindow = Window & {
  __EDITOR_E2E_PROBE__?: boolean
  __editorItemControlOverlay?: { itemIds: string[]; selectedItems: string[] }
  __editorSetTextBoxSizing?: (
    boxId: string,
    patch: EditorTextSizingPatch,
  ) => Promise<boolean>
}

const hasRegisteredGeometry = (
  controlState: ItemControlState | null | undefined,
): boolean =>
  !!controlState?.position &&
  (controlState.position.width ?? 0) > 0 &&
  (controlState.position.height ?? 0) > 0

type ItemControlsWrapperProps = {
  itemId: string
  zoom: number
}

/**
 * Wrapper that reads item control state and renders full controls for selected items.
 * Separated to allow individual item state subscriptions.
 */
const ItemControlsWrapper: FC<ItemControlsWrapperProps> = ({
  itemId,
  zoom,
}) => {
  const controlState = useRecoilValue(itemControlStateAtomFamily(itemId))

  if (!hasRegisteredGeometry(controlState)) {
    return null
  }

  return (
    <SelectedItemControls
      itemId={itemId}
      controlState={controlState}
      zoom={zoom}
    />
  )
}

type ItemWarningsWrapperProps = {
  itemId: string
  selectedItemsSet: Set<string>
  zoom: number
}

/**
 * Wrapper that renders ONLY warnings for non-selected items.
 * Selected items get their warnings rendered via SelectedItemControls.
 */
const ItemWarningsWrapper: FC<ItemWarningsWrapperProps> = ({
  itemId,
  selectedItemsSet,
  zoom,
}) => {
  const controlState = useRecoilValue(itemControlStateAtomFamily(itemId))

  // Skip if item is selected (warnings rendered via SelectedItemControls)
  if (selectedItemsSet?.has(itemId)) {
    return null
  }

  if (!hasRegisteredGeometry(controlState)) {
    return null
  }

  // Skip if no warnings to show
  const hasWarnings =
    controlState.warnings?.showBleedWarning ||
    controlState.image?.showPrintQualityWarning

  if (!hasWarnings) {
    return null
  }

  const { x, y, width, height, rotate } = controlState.position
  // A classic item keeps the staging bar, which takes the pointer over the
  // item's corner; the editor items' bar lets clicks through to the box.
  const Bar = isEditorItemType(controlState.type)
    ? WarningBar
    : ClassicWarningBar

  return (
    <ItemWrapper
      $x={x}
      $y={y}
      $width={width}
      $height={height}
      $rotate={rotate}
      data-item-id={itemId}
      data-item-control-overlay
    >
      <Bar $zoom={zoom} data-warning-bar>
        <WarningIndicators
          itemId={itemId}
          itemType={controlState.type}
          imageState={controlState.image}
          warnings={controlState.warnings}
        />
      </Bar>
    </ItemWrapper>
  )
}

type ItemHideInPdfWrapperProps = {
  itemId: string
  selectedItemsSet: Set<string>
  zoom: number
}

/**
 * Wrapper that renders a hide-in-PDF badge for items with hideInPdf: true.
 * Only renders for non-selected items; selected items get their badge via SelectedItemControls.
 */
const ItemHideInPdfWrapper: FC<ItemHideInPdfWrapperProps> = ({
  itemId,
  selectedItemsSet,
  zoom,
}) => {
  const { t } = useTranslation()
  const controlState = useRecoilValue(itemControlStateAtomFamily(itemId))

  // Skip if item is selected (badge rendered via SelectedItemControls)
  if (selectedItemsSet?.has(itemId)) return null

  if (!controlState?.hideInPdf || !hasRegisteredGeometry(controlState))
    return null

  const { x, y, width, height, rotate } = controlState.position

  return (
    <ItemWrapper
      $x={x}
      $y={y}
      $width={width}
      $height={height}
      $rotate={rotate}
      data-item-id={itemId}
      data-item-control-overlay
    >
      <HideInPdfBadge $zoom={zoom}>
        <Tooltip
          text={t('editor.menus.properties.elements.hideInPdfLabel')}
          placement="right"
          offsetX={10}
          width="120px"
        >
          <Icon icon="eyeSlash" width="16px" height="16px" />
        </Tooltip>
      </HideInPdfBadge>
    </ItemWrapper>
  )
}

/**
 * Overlay that renders control UI (borders, icons, tooltips) above all items.
 * Ensures controls are always visible regardless of item z-index values.
 *
 * Architecture:
 * - Items register their state via useItemControlState hook
 * - Overlay reads state from itemControlStateAtomFamily
 * - Uses pointer-events: none on container, auto on interactive elements
 * - Warnings are shown for ALL items, controls only for selected items
 */
export const ItemControlOverlay: FC = () => {
  const allItemIds = useRecoilValue(itemIdAtom)
  const selectedItems = useRecoilValue(selectedItemsAtom)
  const zoom = useRecoilValue(pageZoomAtom)

  // O(1) lookup for selected items instead of O(n) array.includes()
  const selectedItemsSet = useMemo(
    () => new Set(selectedItems),
    [selectedItems],
  )

  useEffect(() => {
    const probeWindow = window as ProbeWindow
    if (!probeWindow.__EDITOR_E2E_PROBE__) return
    probeWindow.__editorItemControlOverlay = {
      itemIds: allItemIds,
      selectedItems,
    }
  }, [allItemIds, selectedItems])

  // Fill is data-only: no control writes it. The armed probe hands a spec
  // that must set a mode mid-test, on a box with no row to seed, the road the
  // drawer's maximum field writes through (`useEditorSetTextBoxSizing`) —
  // the box's draft and the unsaved flag, no history step. No spec uses it
  // today; every spec that needs Fill seeds it on the row.
  const setTextBoxSizing = useEditorSetTextBoxSizing()
  useEffect(() => {
    const probeWindow = window as ProbeWindow
    if (!probeWindow.__EDITOR_E2E_PROBE__) return
    probeWindow.__editorSetTextBoxSizing = setTextBoxSizing
    return () => {
      if (probeWindow.__editorSetTextBoxSizing === setTextBoxSizing)
        delete probeWindow.__editorSetTextBoxSizing
    }
  }, [setTextBoxSizing])

  // Don't render if no items exist
  if (!allItemIds?.length) {
    return null
  }

  return (
    <Fragment>
      {/* Render hide-in-PDF badges for ALL items */}
      {allItemIds.map((itemId: string) => (
        <ItemHideInPdfWrapper
          key={`hide-pdf-${itemId}`}
          itemId={itemId}
          selectedItemsSet={selectedItemsSet}
          zoom={zoom}
        />
      ))}

      {/* Render warnings for ALL non-selected items */}
      {allItemIds.map((itemId: string) => (
        <ItemWarningsWrapper
          key={`warnings-${itemId}`}
          itemId={itemId}
          selectedItemsSet={selectedItemsSet}
          zoom={zoom}
        />
      ))}

      {/* Render full controls for selected items */}
      {selectedItems.map((itemId: string) => (
        <ItemControlsWrapper
          key={`controls-${itemId}`}
          itemId={itemId}
          zoom={zoom}
        />
      ))}

      {/* The confirmation the classic bar's conversion control opens,
          mounted here so the conversion it starts outlives that control. */}
      <EditorTextBoxConvertModal />
    </Fragment>
  )
}
